// Electron SQLite Manager for ACCTOO
// Uses Electron IPC to communicate with main process SQLite database

class ElectronSQLiteManager {
  constructor() {
    this.userId = null;
    this.companyId = null;
    this.deviceId = null;
    this.isInitialized = false;
  }

  // Check if running in Electron
  isElectron() {
    return typeof window !== 'undefined' && window.electronAPI;
  }

  // Initialize SQLite database
  async init(userId, companyId, deviceId) {
    if (!this.isElectron()) {
      throw new Error('Electron SQLite Manager requires Electron environment');
    }

    try {
      this.userId = userId;
      this.companyId = companyId;
      this.deviceId = deviceId;

      console.log('🔄 Initializing Electron SQLite...', { userId, companyId, deviceId });

      const result = await window.electronAPI.sqliteInit(userId, companyId, deviceId);

      if (result.success) {
        this.isInitialized = true;
        console.log('✅ Electron SQLite initialized successfully');
        return { success: true, dbPath: result.dbPath };
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('❌ Electron SQLite initialization failed:', error);
      throw error;
    }
  }

  // =============================================
  // DATA OPERATIONS
  // =============================================

  // Insert data
  async insert(tableName, data) {
    if (!this.isInitialized) {
      throw new Error('SQLite not initialized');
    }

    try {
      // Add sync context
      const dataWithContext = {
        ...data,
        company_id: this.companyId,
        device_id: this.deviceId,
        sync_status: 'pending',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const result = await window.electronAPI.sqliteInsert(tableName, dataWithContext);

      if (result.success) {
        console.log(`✅ Record inserted in ${tableName}:`, data.id);
        return result;
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error(`❌ Insert failed for ${tableName}:`, error);
      throw error;
    }
  }

  // Update data
  async update(tableName, id, data) {
    if (!this.isInitialized) {
      throw new Error('SQLite not initialized');
    }

    try {
      // Add sync context
      const dataWithContext = {
        ...data,
        updated_at: new Date().toISOString()
      };

      const result = await window.electronAPI.sqliteUpdate(tableName, id, dataWithContext);

      if (result.success) {
        console.log(`✅ Record updated in ${tableName}:`, id);
        return result;
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error(`❌ Update failed for ${tableName}:`, error);
      throw error;
    }
  }

  // Select data
  async select(tableName, filters = {}) {
    if (!this.isInitialized) {
      throw new Error('SQLite not initialized');
    }

    try {
      let whereClause = '';
      const params = [];

      if (filters.company_id) {
        whereClause = 'company_id = ?';
        params.push(filters.company_id);
      }

      if (filters.device_id) {
        if (whereClause) whereClause += ' AND ';
        whereClause += 'device_id = ?';
        params.push(filters.device_id);
      }

      if (filters.id) {
        if (whereClause) whereClause += ' AND ';
        whereClause += 'id = ?';
        params.push(filters.id);
      }

      const result = await window.electronAPI.sqliteSelect(tableName, whereClause, params);

      if (result.success) {
        console.log(`✅ Records selected from ${tableName}:`, result.data.length);
        return result.data;
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error(`❌ Select failed for ${tableName}:`, error);
      throw error;
    }
  }

  // Delete data
  async delete(tableName, id) {
    if (!this.isInitialized) {
      throw new Error('SQLite not initialized');
    }

    try {
      const result = await window.electronAPI.sqliteDelete(tableName, id);

      if (result.success) {
        console.log(`✅ Record deleted from ${tableName}:`, id);
        return result;
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error(`❌ Delete failed for ${tableName}:`, error);
      throw error;
    }
  }

  // =============================================
  // BILL NUMBER GENERATION
  // =============================================

  // Generate bill number
  async generateBillNumber(billType) {
    if (!this.isInitialized) {
      throw new Error('SQLite not initialized');
    }

    try {
      const result = await window.electronAPI.sqliteGenerateBillNumber(billType);

      if (result.success) {
        console.log('✅ Bill number generated:', result.billNumber);
        return result.billNumber;
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('❌ Bill number generation failed:', error);
      throw error;
    }
  }

  // =============================================
  // UTILITY METHODS
  // =============================================

  // Check if initialized
  isReady() {
    return this.isInitialized;
  }

  // Get current context
  getContext() {
    return {
      userId: this.userId,
      companyId: this.companyId,
      deviceId: this.deviceId,
      environment: 'electron'
    };
  }

  // Close database
  async close() {
    if (!this.isInitialized) {
      return;
    }

    try {
      await window.electronAPI.sqliteClose();
      this.isInitialized = false;
      console.log('✅ Electron SQLite closed');
    } catch (error) {
      console.error('❌ Close database failed:', error);
      throw error;
    }
  }
}

export default ElectronSQLiteManager;
